<template>
    <div v-if="loaded">
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-date-picker
                            :end-validations="validations.periodo"
                            :max-date="additionals.dataAtual"
                            :range-inline="false"
                            :start-validations="validations.periodo"
                            end-label="Até"
                            range
                            start-label="Período de"
                            v-model="model.periodo"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.unidades"
                            @change="loadFrom('unidade')"
                            default-option-text="Todas"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.infraestruturas"
                            @change="loadFrom('infraestrutura')"
                            default-option-text="Todos"
                            label="Local de atendimento"
                            show-default-option
                            text-field="nome"
                            v-model="model.infraestruturaId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.ocupacoes"
                            @change="loadFrom('ocupacao')"
                            default-option-text="Todas"
                            label="CBO"
                            show-default-option
                            text-field="descricao"
                            v-model="model.ocupacaoId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.especialidades"
                            @change="loadFrom('especialidade')"
                            default-option-text="Todas"
                            label="Especialidade"
                            show-default-option
                            text-field="nome"
                            v-model="model.especialidadeId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.usuarios"
                            @change="loadFrom('usuario')"
                            default-option-text="Todos"
                            label="Profissional"
                            show-default-option
                            text-field="nome"
                            v-model="model.usuarioId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="4">
                        <rkm-form-multiselect
                            :load-all-automatically="false"
                            :options="form.feridas"
                            :select-limit="null"
                            label="Tipo de ferida"
                            option-name-key="nome"
                            option-text-selecteds="feridas"
                            v-model="model.feridasId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-select
                            :options="form.status.cicatrizada"
                            default-option-text="Todos"
                            show-default-option
                            label="Status da ferida"
                            text-field="cicatrizada"
                            v-model="model.statusId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-multiselect
                            :load-all-automatically="false"
                            :options="form.procedimentos"
                            :select-limit="null"
                            label="Procedimentos"
                            option-name-key="descricao"
                            option-text-selecteds="procedimentos"
                            v-model="model.procedimentoCodigo"
                            value-field="codigo"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-input-autocomplete
                            :repository="repositories.relatorioRastreioFerida.paciente"
                            :request-query-string-params="filters"
                            :search-fields="pacienteSearchFields"
                            :text-field="p => `${p.nome}${p.nomeSocial ? ` (${p.nomeSocial})` : ''}${p.cns ? ` - ${p.cns}` : ''}`"
                            label="Paciente"
                            placeholder="Nome, Nome social, CNS, CPF, Data de nascimento ou Prontuário"
                            v-model="model.paciente"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :label="false"
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="reset"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <template v-if="itens">
            <rkm-area
                no-margin-top
                title="Resultado"
            >
                <template v-slot:trailing>

                    <rkm-dropdown
                        class="mr-2"
                        color="primary"
                        cols="1"
                        menu-class="auto-scroll"
                        no-caret
                        small
                        title="Colunas"
                        v-rkm-tooltip.hover
                    >
                        <template v-slot:button-content>
                            <rkm-icon icon="columns" />
                        </template>

                        <rkm-form-checkbox
                            :indeterminate="indeterminate"
                            @change="selectAll"
                            class="mx-2"
                            v-model="selectedAll"
                        >
                            Todas
                        </rkm-form-checkbox>

                        <rkm-form-checkbox-group
                            :options="optionalColumns"
                            class="mx-2"
                            stacked
                            text-field="label"
                            v-model="selectedCols"
                            value-field="key"
                        />
                    </rkm-dropdown>

                    <rkm-button
                        :disabled="itens.length === 0"
                        @click="print"
                        color="info"
                        small
                        title="Imprimir"
                    >
                        <rkm-icon icon="print" />
                    </rkm-button>

                    <rkm-button
                        :disabled="itens.length === 0"
                        @click="exportTo"
                        color="info"
                        small
                        title="Exportar"
                    >
                        <rkm-icon icon="file-export" />
                    </rkm-button>
                </template>

                <rkm-table
                    :fields="fields"
                    :items="itens"
                    :sort-by="sortBy"
                    :sort-desc="sortDesc"
                    @sort-changed="onSortChanged"
                >

                    <template v-slot:cell(diasEvolucao)="{item}">         
                            {{item.diasEvolucao + ' dias'}}
                    </template>

                    <template v-slot:cell(cicatrizada)="{item}">         
                            {{item.cicatrizada == enums.statusFerida.ATIVA ? 'Ativa' : 'Cicatrizada'}}
                    </template>

                     <template v-slot:cell(materiais)="{item}">
                        <div
                            v-for="(material, index) in item.materiais"
                            :key="index"
                        >
                            <span v-if="material.materialNome">
                                <div v-for="(codigo, codigoIndex) in material.materialNome.split(',')" :key="codigoIndex">
                                    {{ codigo.trim() }}
                                    <br v-if="codigoIndex < material.materialNome.split(',').length - 1" />
                                </div>
                            </span>
                        </div>
                    </template>

                    <template v-slot:cell(valorTotal)="item">

                        {{'R$ ' + item.item.valorTotal }}

                        <rkm-icon
                            icon="info"
                            color="secondary"
                            class="infor"
                            small
                            v-rkm-popover.hover.left="{
                                html:true,
                                content: 'Cálculo automático somando valor SIGTAP dos procedimentos vinculados ao atendimento e custo dos materiais utilizados.'
                            }"
                        />                        
                    </template>

                </rkm-table>

            </rkm-area>

            <rkm-area
                no-margin-top
                title="Totais"
                type="fieldset"
            >
                <rkm-row>
                    <rkm-col>
                        <rkm-form-input
                            :value="totalizadores.pacientesDistintosAtendidos"
                            disabled
                            label="Pacientes distintos atendidos"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            :value="totalizadores.atendimentosRealizados"
                            disabled
                            label="Atendimentos realizados"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            :value="totalizadores.soapsGeradas"
                            disabled
                            label="SOAPs geradas"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            :value="totalizadores.feridasRegistradas"
                            disabled
                            label="Feridas registradas"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            :value="totalizadores.procedimentosLancados"
                            disabled
                            label="Procedimentos lançados"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            :value="totalizadores.kitsMateriaisConsumidos"
                            disabled
                            label="Kits/materiais consumidos"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-input
                            :value="totalizadores.valorCustoTotal"
                            disabled
                            label="Valores de custo total"
                        />
                    </rkm-col>
                </rkm-row>
            </rkm-area>
        </template>
    </div>
</template>

<script>
import { mapActions } from 'vuex'
import { repository, formats, error, data, object, stringCase, file } from '@/mixins'

// Enums.
import statusFerida from '@/enums/ambulatorio/feridaStatus'

export default {
    computed: {
        optionalColumns() {

            return this.columns.filter(c => !(c.fixed || false))
        },
        fields() {

            return this.columns.filter(c => (c.fixed || false) || this.selectedCols.includes(c.key))
        },
        validations() {
            return {
                periodo: { required: true },
            }
        },
        pacienteSearchFields() {

            return [
                { key: 'cidadao.nome', searchable: true },
                { key: 'cidadao.nomeSocial', searchable: true },
                { key: 'cidadao.cns', searchable: true },
                { key: 'cidadao.cpf', searchable: true },
                { key: 'cidadao.data_nascimento', searchable: true },
                { key: 'paciente.prontuario', searchable: true },
            ]
        },
        filters() {

            return this.camelObjectKeysToSnakeRecursive({
                
                pacienteId: this.model.paciente?.id ?? null,

            })
        },
    },
    created() {

        this.repositories = {
            relatorioRastreioFerida: this.getConectaSusRepository('ambulatorio.relatorioRastreioFerida'),
            impressao: this.getConectaSusRepository('impressao.ambulatorio'),
            exportacao: this.getConectaSusRepository('exportacao.ambulatorio')
        }

        this.load()
    },
    data() {

        return {
            loaded: false,
            additionals: null,
            form: {
                status: {
                    cicatrizada: [
                        { id: 0, cicatrizada: 'Ativa' },
                        { id: 1, cicatrizada: 'Cicatrizada' },
                    ],
                },
            },
            model: null,
            initials: {
                form: null,
                model: null,
            },
            selectedAll: false,
            indeterminate: true,
            selectedCols: [
                'data',
                'pacienteNome',
                'unidadeNomeFantasia',
                'usuarioNome',
                'feridaNome',
                'cicatrizada',
            ],
            enums: {
                statusFerida,
            },
            columns: [
                {
                    key: 'data',
                    label: 'Data',
                    sortable: true,
                    class: 'col-min',
                    filterByFormatted: true,
                    fixed: true,
                },
                { key: 'pacienteNome', label: 'Paciente', sortable: true},
                { key: 'pacienteProntuario', label: 'Prontuário', sortable: true},
                { key: 'pacienteDataNascimento', label: 'Data de nascimento', sortable: true},
                { key: 'unidadeNomeFantasia', label: 'Unidade', sortable: true},
                { key: 'usuarioNome', label: 'Profissional', sortable: true},
                { key: 'especialidadeNome', label: 'Especialidade', sortable: true},
                { key: 'ocupacaoCodigo', label: 'CBO', sortable: true},
                { key: 'feridaNome', label: 'Ferida', sortable: true},
                { key: 'cicatrizada', label: 'Status', sortable: true},
                { key: 'diasEvolucao', label: 'Dias de evolução', sortable: true},
                { key: 'procedimentosCodigo', label: 'Procedimento', sortable: true},
                { key: 'materiais', label: 'Materiais', sortable: true},
                { key: 'valorTotal', label: 'Custo do atendimento', class: 'text-center', sortable: true},
            ],
            itens: null,
            sortBy: 'data',
            sortDesc: false,
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            this.repositories.relatorioRastreioFerida.list()
                .then(({ data }) => {

                    this.additionals = data.additionals
                    this.form = data.form
                    this.model = data.model

                    this.initials.form = this.clone(data.form)
                    this.initials.model = this.clone(data.model)

                    this.addWatchers()

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        addWatchers() {

            this.$watch('model.periodo', {
                deep: true,
                handler(periodo) {

                    if (periodo.start === null || periodo.end === null) {

                        this.resetAfterPeriodo()
                    }
                    else {

                        this.loadFrom('periodo')
                    }
                }
            })
        },
        loadFrom(field) {          

            this.setLoading()

            this.repositories.relatorioRastreioFerida.list({ ...this.prepareData(), load_from: this.camelToSnake(field) })
                .then(({ data }) => {

                    if ('form' in data) {

                        Object.keys(data.form).forEach(key => this.$set(this.form, key, data.form[key]))
                    }

                     if (this.form.status && Array.isArray(this.form.status)) {

                        this.form.status.cicatrizada = this.form.status.map(item => ({
                            id: item.cicatrizada,
                            cicatrizada: item.cicatrizada === this.enums.statusFerida.ATIVA ? 'Ativa' : 'Cicatrizada',
                        }))
                    }

                    Object.keys(data.model).forEach(key => this.$set(this.model, key, data.model[key]))
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        search() {

            this.setLoading()

            this.repositories.relatorioRastreioFerida.index(this.prepareData())
                .then(({ data }) => {
                    this.itens = data.atendimentos
                    this.totalizadores = data.totalizadores 
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        reset() {

            this.form = this.clone(this.initials.form)
            this.model = this.clone(this.initials.model)

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
        resetAfterPeriodo() {

            this.form = this.clone(this.initials.form)

            const model = this.clone(this.initials.model)

            delete model.periodo

            Object.keys(model).forEach(key => this.$set(this.model, key, model[key]))
        },
        print() {

            this.setLoading()

            this.repositories.impressao.relatorioRastreioFerida(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        exportTo() {

            this.setLoading()

            this.repositories.exportacao.relatorioRastreioFerida(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        prepareDataWithColumns() {

            const data = this.prepareData()

            console.log(data)

            data.columns = this.fields.map(f => ({
                key: this.camelToSnake(f.key),
                label: f.label,
                class: f.class
            }))

            return data
        },
        prepareData() {

            const data = this.clone(this.model)

            data.sortBy = this.sortBy ? this.camelToSnake(this.sortBy) : null
            data.sortDesc = this.sortDesc

            this.formateDates(data.periodo, ['start', 'end'], 'DD/MM/YYYY')

            return this.camelObjectKeysToSnakeRecursive(data)
        },
        selectAll(selected) {

            this.selectedCols = selected ? this.optionalColumns.map(c => c.key) : []
        },
        onSortChanged(context) {
            this.sortBy = context.sortBy
            this.sortDesc = context.sortDesc
        },
    },
    mixins: [repository, formats, error, data, object, stringCase, file],
    watch: {
        model: {
            deep: true,
            handler() {

                this.itens = null
            }
        },
        selectedCols(newValue) {

            this.selectedAll = newValue.length > 0 && newValue.length == this.optionalColumns.length
            this.indeterminate = newValue.length > 0 && newValue.length != this.optionalColumns.length
        },
    }
}
</script>

<style scoped>
.dropdown >>> .auto-scroll {
    max-height: 400px;
    overflow-y: auto;
    width: 400px;
}
.infor {
    height: 15px;
    width: 15px !important;
    right: 8px;
    top: 5px;
    border-radius: 31px;
    padding: 3px;
    border: 1px solid #c2c2c2;
}
</style>